/**
 * `/roster` — the manager-facing week × employee shift grid, Task T2-E-web
 * (location-attendance pillar, pillars-deepening program). T2-E (commit 95618da)
 * shipped `GET /shifts/roster`, the assignment lifecycle and the Vietnam Labour Code
 * soft warnings with no web caller; the web still showed a shift only inside the
 * attendance `DayDrawer`, one employee and one day at a time. This screen is the
 * roster read at its own width.
 *
 * `nav/menu.ts` gates this item on the same anyOf `/attendance`, `/presence` and
 * `/people` use — `[workforce.view_self, workforce.view_department,
 * workforce.view_workspace]` — with no role check: a plain employee reaches this
 * screen too, and the server's `resolved` scope (self ⊂ department ⊂ company)
 * narrows the answer to exactly one row, themself.
 *
 * ── Filters live in the URL, the `PeopleScreen.tsx` convention ───────────────
 *
 * `week_start` and `department_id` are URL query parameters, read through
 * `useSearchParams` and written back on every change — the freshest precedent this
 * task was told to copy. `week_start` defaults to the Monday on or before "today" in
 * the company's timezone.
 *
 * ── `week_start_dow` is not on the wire, so this screen learns it from a 400 ──
 *
 * `SessionUser` (openapi.yaml, `api/types.ts`) publishes no `week_start_dow` field —
 * the shell has nothing to read it from — so this screen starts every session
 * assuming Monday. A company whose real value differs answers `400
 * WORKFORCE_VALIDATION_FAILED`, and — this is the part fix round 1 added —
 * `weekStartDowFromError` (`roster/roster.ts`) parses the company's actual
 * `week_start_dow` straight out of `details.issues[]`, the one place it is knowable
 * at all: `ApiErrorNote` only ever shows the top-level `message`, which is the same
 * fixed generic sentence for every validation failure, so the specific correction
 * the server already sent would otherwise reach no one. Learned once, the value is
 * remembered per company (`localStorage`, a per-viewer convenience, never a
 * credential) and the request is retried with the corrected week — automatically,
 * with no dead end and no button to press. A `week_start` already explicit in the
 * URL (a shared link, a browser back) is corrected onto the nearest right day for
 * that same request rather than discarded back to "today".
 *
 * ── Manage affordances are gated on the permission, never on the role ─────────
 *
 * The cell click that opens the assignment drawer renders only when the session's
 * `permissions` includes `workforce.manage_shifts` — `nav/menu.ts`'s own boxed
 * comment states why for the whole app: hiding a control is honesty, not
 * enforcement, and the server refuses `POST`/`DELETE /shift-assignments` on its own
 * regardless. A `403` on the roster read itself renders through the ordinary
 * `ApiErrorNote` path, never swallowed.
 *
 * ── Warnings are information, not judgement ───────────────────────────────────
 *
 * No sorting or ranking employees by warning count, and no count in the header —
 * `roster/roster.ts`'s own header states the research guardrail this screen exists to
 * honour. `Badge` is always passed the presentation entry's own `.icon`, never
 * `SEVERITY_ICON[…severity]` — the exact review finding T2-B's own fix round 1 named.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { useI18n } from '../i18n';
import { formatDay, localDateIn } from '../i18n/datetime';
import { isoWeekdayOf } from '../attendance/month';
import { useSession } from '../auth/SessionProvider';
import { employeeDepartmentsPath, type EmployeeDepartmentFacetList } from '../api/employees';
import {
  shiftRosterPath,
  shiftsPath,
  type ShiftListResponse,
  type ShiftRoster,
  type ShiftRosterDay,
  type ShiftRosterEmployee,
} from '../api/roster';
import { useApiQuery } from '../api/useApiQuery';
import {
  minutesToHHMM,
  readStoredWeekStartDow,
  shiftWeek,
  storeWeekStartDow,
  viaHintKey,
  weekEnd,
  weekStartDowFromError,
  weekStartOnOrBefore,
} from '../roster/roster';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { EmptyState } from '../states/EmptyState';
import { LastSync } from '../states/LastSync';
import { Skeleton } from '../states/Skeleton';
import { ScreenHeading } from '../shell/ScreenHeading';
import { RosterDrawer } from './roster/RosterDrawer';
import { WarningChipList } from './roster/WarningChips';

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isLocalDateShape(value: string | null): value is string {
  return value !== null && LOCAL_DATE_PATTERN.test(value);
}

/** Monday first, the same catalogue the attendance month grid reads. */
const WEEKDAY_KEYS = [
  'k3.weekday.1',
  'k3.weekday.2',
  'k3.weekday.3',
  'k3.weekday.4',
  'k3.weekday.5',
  'k3.weekday.6',
  'k3.weekday.7',
] as const;

/**
 * A day column head: the weekday and the day of the month, with the full date in
 * words kept for a screen reader. Seven columns of `2026-08-24` spell the same year
 * and month seven times and leave no width for the shift underneath; the week the
 * numbers belong to is already printed, in full, in the range line above the grid.
 */
function DayColumnHead({ date }: { readonly date: string }): ReactNode {
  const { t, locale } = useI18n();
  const key = WEEKDAY_KEYS[isoWeekdayOf(date) - 1] ?? WEEKDAY_KEYS[0];
  return (
    <th scope="col">
      <span className="wp-b-daycol">
        <span className="wp-b-daycol__weekday">{t(key)}</span>
        <span className="wp-b-daycol__day">{date.slice(8, 10)}</span>
      </span>
      <span className="wp-visually-hidden">
        {formatDay(date, locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </span>
    </th>
  );
}

function RosterCell({
  day,
  testPrefix,
}: {
  readonly day: ShiftRosterDay;
  readonly testPrefix: string;
}): ReactNode {
  const { t } = useI18n();
  const hintKey = viaHintKey(day.via);
  return (
    <>
      {day.shift === null ? (
        <p className="wp-small" data-testid={`${testPrefix}-label`}>
          {day.via === null ? t('roster.cell.unconfigured') : t('roster.cell.dayOff')}
        </p>
      ) : (
        <p data-testid={`${testPrefix}-label`}>
          <code>{day.shift.code}</code> {day.shift.start_time}–{day.shift.end_time}
        </p>
      )}
      {hintKey === null ? null : (
        <p className="wp-small wp-muted" data-testid={`${testPrefix}-via`}>
          {t(hintKey)}
        </p>
      )}
      <WarningChipList warnings={day.warnings} testPrefix={testPrefix} />
    </>
  );
}

export interface RosterScreenProps {
  /** The clock, as a parameter — the same seam `AttendanceScreen.tsx` uses, so a test can hold "today" still. */
  readonly nowMs?: number;
}

export function RosterScreen({ nowMs }: RosterScreenProps = {}): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';
  const permissions = user?.permissions ?? [];
  const mayManage = permissions.includes('workforce.manage_shifts');

  const companyId = user?.active_company_id ?? 'unknown';
  // The company's own `week_start_dow` — never published on the wire (this file's
  // own header) — learned, if at all, from a previous `400` on this very read, and
  // remembered per company so a returning visitor never sees the wrong-guess flash
  // twice. Monday until then.
  const [weekStartDow, setWeekStartDow] = useState(() => readStoredWeekStartDow(companyId) ?? 1);

  const today = useMemo(() => localDateIn(timezone, nowMs ?? Date.now()), [timezone, nowMs]);
  const defaultWeekStart = useMemo(
    () => weekStartOnOrBefore(today, weekStartDow),
    [today, weekStartDow],
  );

  const [params, setParams] = useSearchParams();
  const weekStartParam = params.get('week_start');
  const weekStart = isLocalDateShape(weekStartParam) ? weekStartParam : defaultWeekStart;
  const departmentId = params.get('department_id');

  const setParam = useCallback(
    (updates: Readonly<Record<string, string | null>>) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      });
    },
    [setParams],
  );

  const [rosterToken, setRosterToken] = useState(0);
  const rosterPath = shiftRosterPath(weekStart, departmentId ?? undefined);
  const roster = useApiQuery<ShiftRoster>(rosterPath, rosterToken);

  // Self-correction: a wrong Monday-guess comes back `400
  // WORKFORCE_VALIDATION_FAILED` naming the company's real `week_start_dow` in
  // `details.issues[]` — the one place that value is knowable at all. Two
  // independent things follow from a parsed `learnedDow`, and fix round 2 exists
  // because round 1 conflated them into one guard:
  //
  //  1. **Learn it**, if it is not already what this session assumes — store it per
  //     company and update state. Skipped once known, so a company the server keeps
  //     refusing for some other reason never re-stores the same value in a loop.
  //  2. **Correct the explicit URL value**, if one is present and does not conform to
  //     `learnedDow` — independently of whether step 1 just ran or the dow was
  //     already known. A stale bookmark or a shared link built before the dow was
  //     ever learned still names a `week_start` that will not conform even after
  //     `weekStartDow` state is correct, and round 1's single guard
  //     (`learnedDow === weekStartDow` ⇒ skip everything) skipped this step too in
  //     exactly that case — a permanent, silent dead end for that one URL. This
  //     branch's own guard is `weekStartOnOrBefore(weekStartParam, learnedDow) ===
  //     weekStartParam` (already conforms ⇒ nothing to correct), which cannot loop:
  //     a value this function just produced always conforms to the `dow` it was
  //     asked for.
  //
  // Both steps run only while `roster.status` is `'error'`, so a request that
  // actually succeeds never touches either.
  useEffect(() => {
    if (roster.status !== 'error' || roster.error === null) return;
    const learnedDow = weekStartDowFromError(roster.error);
    if (learnedDow === null) return;

    if (learnedDow !== weekStartDow) {
      storeWeekStartDow(companyId, learnedDow);
      setWeekStartDow(learnedDow);
    }

    if (weekStartParam !== null) {
      const conforming = weekStartOnOrBefore(weekStartParam, learnedDow);
      if (conforming !== weekStartParam) {
        setParam({ week_start: conforming });
      }
    }
  }, [roster.status, roster.error, weekStartDow, companyId, weekStartParam, setParam]);

  const departments = useApiQuery<EmployeeDepartmentFacetList>(employeeDepartmentsPath());

  // The shift catalogue — every shift, not only `active: true` — is fetched once,
  // only for a session that can actually open the drawer that reads it (the assign
  // form's select, and the historical-shift lookup in the assignment list). Reading
  // it costs nothing extra for a session that never opens the drawer.
  const shiftsQuery = useApiQuery<ShiftListResponse>(mayManage ? shiftsPath() : null);
  const shifts = shiftsQuery.data?.data ?? [];

  const [openEmployee, setOpenEmployee] = useState<{ id: string; name: string } | null>(null);

  const bumpRoster = useCallback(() => {
    setRosterToken((value) => value + 1);
  }, []);

  const employees = roster.data?.employees ?? [];
  const days = roster.data?.days ?? [];

  return (
    <div className="wp-stack wp-b">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.roster')}</ScreenHeading>
        {/* Both stamps, both `null`, both printed as "chưa từng" rather than hidden.
            A roster is planned work: it is written by a person through
            `POST /shift-assignments`, not synced from an agent or from achau, and
            this screen reads neither a heartbeat nor a sweep. Inventing a freshness
            for it out of the assignment's `created_at` would be a claim about a
            machine made from somebody's typing (`TimeTrackingScreen.tsx` refuses the
            same thing for the same reason), and hiding the line would read as an
            oversight rather than as the fact it is. */}
        <LastSync agentSyncAt={null} achauSyncAt={null} timezone={timezone} />
      </div>

      <p className="wp-small wp-muted wp-b-lede" data-testid="t2ew-intro">
        {t('roster.intro')}
      </p>

      <fieldset className="wp-filters" data-testid="t2ew-filters">
        <legend>{t('roster.filters.legend')}</legend>

        <div className="wp-field">
          <span className="wp-field__label" data-testid="t2ew-week-range">
            {t('roster.week.range', {
              from: formatDay(weekStart, locale, { day: 'numeric', month: 'long', year: 'numeric' }),
              to: formatDay(weekEnd(weekStart), locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }),
            })}
          </span>
          <div className="wp-filters__actions">
            <button
              type="button"
              className="wp-button"
              data-testid="t2ew-week-prev"
              onClick={() => {
                setParam({ week_start: shiftWeek(weekStart, -1) });
              }}
            >
              {t('roster.week.prev')}
            </button>
            <button
              type="button"
              className="wp-button"
              data-testid="t2ew-week-this"
              disabled={weekStart === defaultWeekStart}
              onClick={() => {
                setParam({ week_start: null });
              }}
            >
              {t('roster.week.thisWeek')}
            </button>
            <button
              type="button"
              className="wp-button"
              data-testid="t2ew-week-next"
              onClick={() => {
                setParam({ week_start: shiftWeek(weekStart, 1) });
              }}
            >
              {t('roster.week.next')}
            </button>
          </div>
        </div>

        <div className="wp-field">
          <label htmlFor="t2ew-department">{t('roster.filter.department')}</label>
          <select
            id="t2ew-department"
            value={departmentId ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setParam({ department_id: value === '' ? null : value });
            }}
            data-testid="t2ew-department"
          >
            <option value="">{t('roster.filter.department.all')}</option>
            {(departments.data?.departments ?? [])
              .filter((facet) => facet.department_id !== null)
              .map((facet) => (
                <option key={facet.department_id} value={facet.department_id ?? ''}>
                  {facet.department_name ?? t('roster.grid.noDepartment')}
                </option>
              ))}
          </select>
        </div>
      </fieldset>

      {roster.status === 'loading' ? (
        <Skeleton
          rows={5}
          variant="block"
          label={t('roster.grid.loading')}
          testId="t2ew-grid-skeleton"
        />
      ) : null}

      {roster.status === 'error' && roster.error !== null ? (
        <ApiErrorNote
          error={roster.error}
          onRetry={roster.reload}
          subject={t('roster.subject')}
          testId="t2ew-grid-error"
        />
      ) : null}

      {roster.status === 'ok' && employees.length === 0 ? (
        <EmptyState
          title={t('roster.grid.empty.title')}
          description={t('roster.grid.empty.body')}
          testId="t2ew-grid-empty"
        />
      ) : null}

      {roster.status === 'ok' && employees.length > 0 ? (
        <div className="wp-table-scroll">
          <table className="wp-table wp-table--wide wp-b-roster" data-testid="t2ew-grid">
            <caption className="wp-visually-hidden">{t('roster.grid.caption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('roster.grid.col.employee')}</th>
                {days.map((date) => (
                  <DayColumnHead key={date} date={date} />
                ))}
                <th scope="col" className="wp-num">
                  {t('roster.grid.col.weekTotal')}
                </th>
              </tr>
            </thead>
            <tbody>
              {employees.map((row: ShiftRosterEmployee) => (
                <tr key={row.employee_id} data-testid={`t2ew-row-${row.employee_id}`}>
                  <th scope="row">
                    <span className="wp-b-roster__name">{row.full_name}</span>
                    <span
                      className="wp-small wp-muted wp-b-roster__dept"
                      data-testid={`t2ew-department-${row.employee_id}`}
                    >
                      {row.department_name ?? t('roster.grid.noDepartment')}
                    </span>
                  </th>
                  {row.days.map((day) => {
                    const testId = `t2ew-cell-${row.employee_id}-${day.date}`;
                    return (
                      <td key={day.date} data-testid={testId}>
                        {mayManage ? (
                          <button
                            type="button"
                            className="wp-linklike wp-b-roster__open"
                            aria-label={t('roster.cell.open', {
                              name: row.full_name,
                              date: formatDay(day.date, locale, {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              }),
                            })}
                            onClick={() => {
                              setOpenEmployee({ id: row.employee_id, name: row.full_name });
                            }}
                            data-testid={`${testId}-open`}
                          >
                            <RosterCell day={day} testPrefix={testId} />
                          </button>
                        ) : (
                          <RosterCell day={day} testPrefix={testId} />
                        )}
                      </td>
                    );
                  })}
                  <td className="wp-num" data-testid={`t2ew-week-total-${row.employee_id}`}>
                    <span className="wp-b-roster__total">
                      {minutesToHHMM(row.week_planned_minutes)}
                    </span>
                    <WarningChipList
                      warnings={row.week_warnings}
                      testPrefix={`t2ew-week-total-${row.employee_id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {openEmployee === null ? null : (
        <RosterDrawer
          employeeId={openEmployee.id}
          employeeName={openEmployee.name}
          shifts={shifts}
          timezone={timezone}
          onClose={() => {
            setOpenEmployee(null);
          }}
          onChanged={bumpRoster}
        />
      )}
    </div>
  );
}
